/* Novah shared interactions — vanilla, no deps */
(function () {
  'use strict';

  /* M14 resilience: only adopt the hidden reveal start-state once this script is actually running.
     If novah.js fails to load/run (e.g. a third-party ScriptTag throws early), .novah-js is never
     added and every .novah-reveal block stays visible instead of stuck at opacity:0. */
  document.documentElement.classList.add('novah-js');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ----- Scroll-triggered reveals ----- */
  function initReveals() {
    if (reduceMotion || !('IntersectionObserver' in window)) {
      document.querySelectorAll('.novah-reveal').forEach(el => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    document.querySelectorAll('.novah-reveal').forEach(el => io.observe(el));
  }

  /* ----- Sticky-on-scroll header ----- */
  function initStickyHeader() {
    const nav = document.querySelector('[data-novah-sticky]');
    if (!nav) return;
    let lastY = 0;
    const onScroll = () => {
      const y = window.scrollY;
      if (y > 80) nav.classList.add('is-stuck'); else nav.classList.remove('is-stuck');
      lastY = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ----- Carousel arrow controls ----- */
  function initCarousels() {
    document.querySelectorAll('[data-novah-carousel]').forEach(root => {
      const track = root.querySelector('.novah-carousel');
      if (!track) return;
      const prev = root.querySelector('[data-carousel-prev]');
      const next = root.querySelector('[data-carousel-next]');
      const step = () => {
        const first = track.querySelector(':scope > *');
        if (!first) return 320;
        const gap = parseFloat(getComputedStyle(track).gap) || 16;
        return first.getBoundingClientRect().width + gap;
      };
      if (prev) prev.addEventListener('click', () => { if (prev.getAttribute('aria-disabled') === 'true') return; track.scrollBy({ left: -step(), behavior: 'smooth' }); });
      if (next) next.addEventListener('click', () => { if (next.getAttribute('aria-disabled') === 'true') return; track.scrollBy({ left: step(), behavior: 'smooth' }); });

      const updateState = () => {
        // NOVAH a11y: aria-disabled (not native disabled) keeps boundary arrows in the tab order (WCAG 2.4.7/4.1.2); clicks no-op above.
        if (prev) prev.setAttribute('aria-disabled', track.scrollLeft <= 4);
        if (next) next.setAttribute('aria-disabled', track.scrollLeft + track.clientWidth >= track.scrollWidth - 4);
      };
      track.addEventListener('scroll', updateState, { passive: true });
      window.addEventListener('resize', updateState);
      updateState();
    });
  }

  /* ----- Mobile nav drawer ----- */
  function initMobileNav() {
    const opener = document.querySelector('[data-novah-nav-toggle]');
    const drawer = document.querySelector('[data-novah-nav-drawer]');
    if (!opener || !drawer) return;
    const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

    function setOpen(open) {
      drawer.classList.toggle('is-open', open);
      document.documentElement.classList.toggle('novah-nav-open', open);
      opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      drawer.inert = !open;
      if (open) {
        const first = drawer.querySelector(FOCUSABLE);
        if (first) first.focus();
      } else {
        opener.focus();
      }
    }

    opener.addEventListener('click', () => setOpen(!drawer.classList.contains('is-open')));
    drawer.querySelectorAll('[data-nav-close]').forEach(b => b.addEventListener('click', () => setOpen(false)));

    document.addEventListener('keydown', (e) => {
      if (!drawer.classList.contains('is-open')) return;
      if (e.key === 'Escape') { setOpen(false); return; }
      if (e.key !== 'Tab') return;
      const items = Array.from(drawer.querySelectorAll(FOCUSABLE)).filter(el => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* ----- Init on DOM ready ----- */
  function init() {
    initReveals();
    initStickyHeader();
    initCarousels();
    initMobileNav();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
